import { expressApi } from '@/state/api/expressApi';
import { triggerBrowserDownload } from '@/shared/utils/triggerBrowserDownload';
import { filenameFromContentDisposition } from '@/shared/utils/filenameFromContentDisposition';
import type { PageDto, TranslatedBatchDto, TranslatedBatchFileDto } from '@/types';

export interface ListBatchesArgs {
  page: number;
  filename?: string;
}

export const translatedBatchesApi = expressApi.injectEndpoints({
  endpoints: (builder) => ({
    getTranslatedBatches: builder.query<PageDto<TranslatedBatchDto>, ListBatchesArgs>({
      query: ({ page, filename }) => ({
        url: '/translated-batches',
        params: {
          page,
          size: 10,
          sort: 'createdAt,desc',
          ...(filename?.trim() ? { filename: filename.trim() } : {}),
        },
      }),
      providesTags: ['TranslatedBatches'],
    }),
    getTranslatedBatchFiles: builder.query<TranslatedBatchFileDto[], string>({
      query: (batchId) => `/translated-batches/${batchId}/files`,
    }),
    // COPA-808: one history row, for the page the HITL-ready email links to. No tags: nothing in
    // the app invalidates 'TranslatedBatches' today, so providing one would promise a refresh that
    // never comes. The id comes from the address bar (react-router decodes %2F back to / in
    // params), so it is encoded to stay one path segment: "..%2Fme" must not become a request
    // to /api/v1/me.
    getTranslatedBatch: builder.query<TranslatedBatchDto, string>({
      query: (batchId) => `/translated-batches/${encodeURIComponent(batchId)}`,
    }),
    downloadBatchZip: builder.mutation<void, { batchId: string; name: string }>({
      queryFn: async ({ batchId, name }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/translated-batches/${batchId}/download`,
          // Blob on success, parsed JSON on failure. A blanket `.blob()` also
          // wrapped error bodies, so an RFC 7807 problem arrived as a Blob and
          // the XTM Cloud guard in the base query could not see it — a rejected
          // XTM Cloud password during a download still ended on "access denied".
          responseHandler: async (response) =>
            response.ok ? response.blob() : response.json().catch(() => undefined),
        });
        if (result.error) {
          return { error: result.error };
        }
        // The server names a partial archive "<label>-translated-1-of-3.zip", which is
        // the only place a partial download announces itself once it is on disk.
        // Constructing the name here would throw that away; it stays as the fallback for
        // a server that sends no header.
        const suggested = filenameFromContentDisposition(
          result.meta?.response?.headers.get('Content-Disposition'),
        );
        triggerBrowserDownload(result.data as Blob, suggested ?? `${name}-translated.zip`);
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useGetTranslatedBatchesQuery,
  useGetTranslatedBatchQuery,
  useGetTranslatedBatchFilesQuery,
  useDownloadBatchZipMutation,
} = translatedBatchesApi;
